import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Users, Car, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useTrip } from "@/context/TripContext";
import { OnRequestPanel } from "@/components/OnRequest";

/** Compact booking widget used in hero + /book page. */
export default function BookingWidget({ compact = false }) {
  const nav = useNavigate();
  const [safaris, setSafaris] = useState([]);
  const [safariId, setSafariId] = useState("");
  const bookingType = "private"; // all safaris are private-jeep only
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    api.get("/safaris").then((r) => {
      const list = r.data.filter((s) => s.booking_mode !== "on_request");
      setSafaris(list);
      if (list.length) setSafariId(list[0].id);
    }).catch((e) => toast.error(formatErr(e)));
  }, []);

  useEffect(() => {
    if (!safariId || !date) return;
    setLoadingSlots(true);
    const d = format(date, "yyyy-MM-dd");
    api.get(`/safaris/${safariId}/availability`, { params: { date: d } })
      .then((r) => { setSlots(r.data); setSlotId(r.data.find((x) => x.remaining_vehicles > 0 || x.remaining_seats > 0)?.id || ""); })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoadingSlots(false));
  }, [safariId, date]);

  const selectedSafari = safaris.find((s) => s.id === safariId);
  const selectedSlot = slots.find((s) => s.id === slotId);
  const seatsPerJeep = selectedSlot?.seats_per_vehicle || selectedSafari?.vehicle_capacity || 6;
  const vehicles = Math.max(1, Math.ceil((adults + children) / seatsPerJeep));
  const { setTrip } = useTrip();
  const canBook = (s) => (bookingType === "private" ? s.remaining_vehicles > 0 : s.remaining_seats > 0);

  useEffect(() => {
    setTrip({
      safari: selectedSafari?.name,
      date: date ? format(date, "dd MMM yyyy") : "",
      slot: selectedSlot ? `${selectedSlot.departure_time} (${selectedSlot.label})` : "",
      type: bookingType,
    });
  }, [selectedSafari, date, selectedSlot, bookingType, setTrip]);

  const handleBook = () => {
    if (!safariId) return toast.error("Select a safari");
    if (!date) return toast.error("Pick a date");
    if (!slotId) return toast.error("Pick a departure time");
    const params = new URLSearchParams({
      safari_id: safariId,
      slot_id: slotId,
      booking_type: bookingType,
      vehicles: String(vehicles),
      adults: String(adults),
      children: String(children),
    });
    nav(`/checkout?${params.toString()}`);
  };

  return (
    <div
      className={`bg-white/95 ${compact ? "p-5 md:p-6" : "p-6 md:p-8"} rounded-2xl shadow-2xl shadow-black/20 ring-1 ring-black/5 backdrop-blur-sm`}
      data-testid="booking-widget"
    >
      <div className="grid gap-4 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="jt-eyebrow mb-2 block">Safari</label>
          <Select value={safariId} onValueChange={setSafariId}>
            <SelectTrigger data-testid="widget-safari-select" className="h-12"><SelectValue placeholder="Choose safari" /></SelectTrigger>
            <SelectContent>
              {safaris.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`widget-safari-opt-${s.slug}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="jt-eyebrow mb-2 block">Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-12 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm bg-white" data-testid="widget-date-trigger">
                <CalendarIcon size={16} className="text-[#5C6B63]"/>
                <span>{date ? format(date, "dd MMM yyyy") : "Pick date"}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                data-testid="widget-date-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label className="jt-eyebrow mb-2 block">Guests</label>
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-12 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm bg-white" data-testid="widget-guests-trigger">
                <Users size={16} className="text-[#5C6B63]"/>
                <span>{adults} Adults · {children} Kids</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-4">
              <GuestsStepper label="Adults" value={adults} setValue={setAdults} min={1} testid="stepper-adults"/>
              <GuestsStepper label="Children" value={children} setValue={setChildren} min={0} testid="stepper-children"/>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {date && (
        <div className="mt-5">
          <label className="jt-eyebrow mb-2 block">Departure slots {loadingSlots && "— loading..."}</label>
          <div className="flex flex-wrap gap-2">
            {slots.length === 0 && !loadingSlots && (
              <span className="text-sm text-[#5C6B63]">No slots available for this date.</span>
            )}
            {slots.length > 0 && slots.every((s) => s.started) && (
              <span className="w-full text-sm text-[#C86D51] font-medium" data-testid="widget-all-started">Today&apos;s safaris have started — no slots left. Please pick another date.</span>
            )}
            {slots.map((s) => {
              const avail = canBook(s);
              return (
                <button
                  key={s.id}
                  disabled={!avail}
                  onClick={() => setSlotId(s.id)}
                  data-testid={`widget-slot-${s.id}`}
                  className={`px-4 py-2 rounded-full text-sm border ${slotId === s.id ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white text-[#1C2421] border-[#E2DACF]"} ${!avail ? "opacity-40 cursor-not-allowed" : "hover:border-[#1E3A2B]"}`}
                >
                  <Clock size={13} className="inline mr-1"/> {s.departure_time} · {s.label} · {inr(s.price)}
                  {s.started ? " · safari started" : !avail && " · sold out"}
                </button>
              );
            })}
          </div>
          {!loadingSlots && !slots.some(canBook) && !(slots.length > 0 && slots.every((s) => s.started)) && selectedSafari && (
            <div className="mt-4">
              <OnRequestPanel safari={selectedSafari} date={format(date, "yyyy-MM-dd")} bookingType={bookingType}
                vehicles={vehicles} adults={adults} children={children} testid="widget-request"/>
            </div>
          )}
        </div>
      )}

      {bookingType === "private" && (
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <label className="jt-eyebrow">Jeeps</label>
          <span className="inline-flex items-center gap-2 border border-[#E2DACF] rounded-full px-3 py-1.5 bg-white text-sm" data-testid="vehicles-val">
            <Car size={14} className="text-[#5C6B63]"/> {vehicles} jeep{vehicles > 1 ? "s" : ""}
          </span>
          <span className="text-xs text-[#5C6B63]" data-testid="vehicles-auto-note">Auto: 1 jeep per {seatsPerJeep} guests</span>
          {selectedSlot && (
            <span className={`text-sm ${vehicles > selectedSlot.remaining_vehicles ? "text-[#C86D51] font-medium" : "text-[#5C6B63]"}`} data-testid="vehicles-left">{selectedSlot.remaining_vehicles} jeeps left</span>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-sm text-[#5C6B63]">
          {selectedSafari && selectedSlot && (
            <span>
              Est. total:&nbsp;
              <strong className="text-[#1C2421] text-base">
                {bookingType === "private"
                  ? inr(selectedSlot.price * vehicles)
                  : inr(selectedSlot.price * (adults + children))}
              </strong>
            </span>
          )}
        </div>
        <button onClick={handleBook} className="jt-pill-accent" data-testid="widget-book-btn">
          Continue to Checkout
        </button>
      </div>
    </div>
  );
}

function GuestsStepper({ label, value, setValue, min, testid }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm">{label}</span>
      <div className="inline-flex items-center gap-3">
        <button className="w-7 h-7 rounded-full border border-[#E2DACF]" data-testid={`${testid}-dec`} onClick={() => setValue(Math.max(min, value - 1))}>−</button>
        <span className="w-5 text-center" data-testid={`${testid}-val`}>{value}</span>
        <button className="w-7 h-7 rounded-full border border-[#E2DACF]" data-testid={`${testid}-inc`} onClick={() => setValue(value + 1)}>+</button>
      </div>
    </div>
  );
}
